import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Lock, CheckCircle, AlertCircle, Loader2, Copy, Eye, EyeOff, Database } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useDocuments } from '../../hooks/useDocuments';
import { encryptFile, storeEncryptionKey } from '../../lib/encryption';
import { uploadToIPFS } from '../../lib/ipfs';
import { Document } from '../../types';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import SecureUploadZone from '../molecules/SecureUploadZone';
import toast from 'react-hot-toast';

interface SecureUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UploadStep {
  id: string;
  label: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface EncryptionKeyInfo {
  documentId: string;
  fileName: string;
  key: string;
  iv: string;
}

const SecureUploadModal: React.FC<SecureUploadModalProps> = ({ isOpen, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadSteps, setUploadSteps] = useState<UploadStep[]>([]);
  const [encryptionKeys, setEncryptionKeys] = useState<EncryptionKeyInfo[]>([]);
  const [showKeys, setShowKeys] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  
  const { addDocument } = useDocuments();
  const { user } = useAuthStore();

  const initializeUploadSteps = (fileCount: number) => {
    const steps: UploadStep[] = [
      { id: 'validation', label: 'Validating files', status: 'pending' },
      { id: 'encryption', label: `Encrypting ${fileCount} file${fileCount !== 1 ? 's' : ''}`, status: 'pending' },
      { id: 'pinata', label: 'Uploading to Pinata IPFS', status: 'pending' },
      { id: 'database', label: 'Saving to database', status: 'pending' },
      { id: 'keys', label: 'Storing encryption keys', status: 'pending' },
      { id: 'complete', label: 'Upload complete', status: 'pending' }
    ];
    setUploadSteps(steps);
    setCurrentStep(0);
  };

  const updateStepStatus = (stepId: string, status: UploadStep['status'], error?: string) => {
    setUploadSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status, error } : step
    ));
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch (error) {
      toast.error(`Failed to copy ${label}`);
    }
  };

  const copyAllKeys = async () => {
    const keysText = encryptionKeys.map(keyInfo => 
      `File: ${keyInfo.fileName}\nDocument ID: ${keyInfo.documentId}\nEncryption Key: ${keyInfo.key}\nIV: ${keyInfo.iv}\n---`
    ).join('\n');
    
    try {
      await navigator.clipboard.writeText(keysText);
      toast.success('All encryption keys copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy encryption keys');
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    console.log('Starting upload process for', files.length, 'files');

    setSelectedFiles(files);
    setIsUploading(true);
    setUploadProgress(0);
    setEncryptionKeys([]);
    setUploadComplete(false);
    
    initializeUploadSteps(files.length);

    try {
      // Step 1: Validation
      updateStepStatus('validation', 'processing');
      setCurrentStep(0);
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate validation time
      updateStepStatus('validation', 'completed');
      setUploadProgress(20);

      // Step 2: Encryption
      setCurrentStep(1);
      updateStepStatus('encryption', 'processing');
      
      const encryptedFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const encryptionResult = await encryptFile(file);
          encryptedFiles.push({
            file,
            ...encryptionResult
          });
          setUploadProgress(20 + (30 * (i + 1) / files.length));
        } catch (error: any) {
          throw new Error(`Encryption failed for ${file.name}: ${error.message}`);
        }
      }
      
      updateStepStatus('encryption', 'completed');
      setUploadProgress(50);

      // Step 3: Pinata IPFS Upload
      setCurrentStep(2);
      updateStepStatus('pinata', 'processing');
      
      const uploadResults = [];
      for (let i = 0; i < encryptedFiles.length; i++) {
        const { file, encryptedData, key, iv } = encryptedFiles[i];
        
        try {
          const ipfsResult = await uploadToIPFS(
            encryptedData, 
            `encrypted_${file.name}`,
            {
              onProgress: (progress) => {
                const baseProgress = 50 + (30 * i / encryptedFiles.length);
                const fileProgress = (30 / encryptedFiles.length) * (progress / 100);
                setUploadProgress(baseProgress + fileProgress);
              }
            }
          );

          if (!ipfsResult.success) {
            throw new Error(ipfsResult.error || 'Pinata IPFS upload failed');
          }

          uploadResults.push({
            file,
            key,
            iv,
            ipfsCid: ipfsResult.ipfsCid!,
            uploadTimestamp: ipfsResult.uploadTimestamp
          });

        } catch (error: any) {
          throw new Error(`Pinata upload failed for ${file.name}: ${error.message}`);
        }
      }
      
      updateStepStatus('pinata', 'completed');
      setUploadProgress(80);

      // Step 4: Database Storage
      setCurrentStep(3);
      updateStepStatus('database', 'processing');
      
      const documentPromises = uploadResults.map(async (result) => {
        const { file, key, iv, ipfsCid, uploadTimestamp } = result;
        
        try {
          // Create document record using the hook - let the database generate the ID
          const newDocument: Omit<Document, 'id'> = {
            name: file.name,
            type: file.type,
            size: file.size,
            uploadedAt: new Date(uploadTimestamp),
            lastModified: new Date(uploadTimestamp),
            isEncrypted: true,
            isNotarized: false,
            tags: ['pinata-ipfs'],
            accessLevel: 'private',
            sharedWith: [],
            ipfsHash: ipfsCid
          };

          // Return a promise that resolves with the actual document ID from the database
          return new Promise((resolve, reject) => {
            addDocument(newDocument, {
              onSuccess: (savedDocument) => {
                // Add to encryption keys display
                setEncryptionKeys(prev => [...prev, {
                  documentId: savedDocument.id,
                  fileName: file.name,
                  key,
                  iv
                }]);
                
                resolve(savedDocument);
              },
              onError: (error) => {
                console.error(`Database storage failed for ${file.name}:`, error);
                reject(new Error(`Database storage failed for ${file.name}: ${error.message}`));
              }
            });
          });

        } catch (error: any) {
          console.error(`Database storage failed for ${file.name}:`, error);
          throw new Error(`Database storage failed for ${file.name}: ${error.message}`);
        }
      });

      // Wait for all documents to be saved
      const savedDocuments = await Promise.all(documentPromises);
      
      updateStepStatus('database', 'completed');
      setUploadProgress(90);

      // Step 5: Store encryption keys in database
      setCurrentStep(4);
      updateStepStatus('keys', 'processing');
      
      // Store each key in the database
      for (const keyInfo of encryptionKeys) {
        try {
          await storeEncryptionKey(keyInfo.documentId, keyInfo.key, keyInfo.iv);
        } catch (error: any) {
          console.error(`Failed to store key for ${keyInfo.fileName}:`, error);
          updateStepStatus('keys', 'error', `Failed to store key for ${keyInfo.fileName}: ${error.message}`);
          throw new Error(`Failed to store encryption key for ${keyInfo.fileName}: ${error.message}`);
        }
      }
      
      updateStepStatus('keys', 'completed');
      setUploadProgress(95);

      // Step 6: Complete
      setCurrentStep(5);
      updateStepStatus('complete', 'processing');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateStepStatus('complete', 'completed');
      setUploadProgress(100);
      setUploadComplete(true);

      toast.success(`Successfully uploaded ${files.length} file${files.length !== 1 ? 's' : ''} to Pinata IPFS`);
      
      // Show encryption keys notification
      toast.success('Encryption keys have been securely stored in the database and your browser.', {
        duration: 8000,
        icon: '🔑'
      });

    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Update current step as error
      const currentStepId = uploadSteps[currentStep]?.id;
      if (currentStepId) {
        updateStepStatus(currentStepId, 'error', error.message);
      }
      
      toast.error(error.message || 'Upload failed');
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setSelectedFiles([]);
    setIsUploading(false);
    setUploadProgress(0);
    setCurrentStep(0);
    setUploadSteps([]);
    setEncryptionKeys([]);
    setShowKeys(false);
    setUploadComplete(false);
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
      resetModal();
    }
  };

  const getStepIcon = (step: UploadStep) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <Card className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Secure Upload to Pinata</h3>
                    <p className="text-gray-600">End-to-end encrypted document storage on IPFS</p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  disabled={isUploading}
                  icon={X}
                />
              </div>

              {/* Security Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                  <Lock className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">AES-256 Encryption</p>
                    <p className="text-sm text-blue-700">Military-grade security</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <Shield className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Zero-Knowledge</p>
                    <p className="text-sm text-green-700">Only you can decrypt</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                  <Database className="h-6 w-6 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">Secure Key Storage</p>
                    <p className="text-sm text-purple-700">Keys stored in database</p>
                  </div>
                </div>
              </div>

              {/* Upload Zone */}
              {!isUploading && !uploadComplete && (
                <SecureUploadZone
                  onFilesSelected={handleFilesSelected}
                  maxFiles={10}
                  maxSize={100 * 1024 * 1024} // 100MB for Pinata
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                />
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="space-y-6">
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Upload Progress</span>
                      <span className="text-sm text-gray-500">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Upload Steps */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Upload Steps</h4>
                    <div className="space-y-3">
                      {uploadSteps.map((step, index) => (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            step.status === 'completed' 
                              ? 'bg-green-50 border border-green-200' 
                              : step.status === 'processing'
                              ? 'bg-blue-50 border border-blue-200'
                              : step.status === 'error'
                              ? 'bg-red-50 border border-red-200'
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {getStepIcon(step)}
                          <div className="flex-1">
                            <p className={`font-medium ${
                              step.status === 'error' ? 'text-red-900' : 'text-gray-900'
                            }`}>
                              {step.label}
                            </p>
                            {step.error && (
                              <p className="text-sm text-red-600 mt-1">{step.error}</p>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Selected Files */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Files ({selectedFiles.length})</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-gray-900 truncate">{file.name}</span>
                            <span className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Encryption Keys Display */}
              {encryptionKeys.length > 0 && (
                <div className="mt-8 space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-green-900">Encryption Keys Securely Stored</h4>
                        <p className="text-sm text-green-800 mt-1">
                          Your encryption keys have been securely stored in both your browser and our database. They are encrypted with your personal key and can only be accessed by you.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">Encryption Keys ({encryptionKeys.length})</h4>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowKeys(!showKeys)}
                        icon={showKeys ? EyeOff : Eye}
                      >
                        {showKeys ? 'Hide' : 'Show'} Keys
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyAllKeys}
                        icon={Copy}
                      >
                        Copy All
                      </Button>
                    </div>
                  </div>

                  {showKeys && (
                    <div className="space-y-4 max-h-64 overflow-y-auto">
                      {encryptionKeys.map((keyInfo, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <h5 className="font-medium text-gray-900 truncate">{keyInfo.fileName}</h5>
                            <span className="text-xs text-gray-500 font-mono">{keyInfo.documentId}</span>
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-medium text-gray-700">Encryption Key</label>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(keyInfo.key, 'Encryption key')}
                                  icon={Copy}
                                  className="h-6 w-6 p-1"
                                />
                              </div>
                              <div className="bg-white border border-gray-300 rounded p-2 font-mono text-xs break-all">
                                {keyInfo.key}
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs font-medium text-gray-700">Initialization Vector (IV)</label>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(keyInfo.iv, 'IV')}
                                  icon={Copy}
                                  className="h-6 w-6 p-1"
                                />
                              </div>
                              <div className="bg-white border border-gray-300 rounded p-2 font-mono text-xs break-all">
                                {keyInfo.iv}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Upload Complete Actions */}
              {uploadComplete && (
                <div className="mt-6 flex gap-3">
                  <Button
                    onClick={() => {
                      setUploadComplete(false);
                      setEncryptionKeys([]);
                      setSelectedFiles([]);
                      setIsUploading(false);
                    }}
                    className="flex-1"
                  >
                    Upload More Files
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      onClose();
                      resetModal();
                    }}
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SecureUploadModal;